/* Which departments a Training Matrix administrator may see.
 *
 * A sibling of GraphClient, like DirectoryClient, and for the same reason:
 * GraphClient is bound to Blueprint's OWN site in its constructor, and this
 * reads and writes a list on a different one.
 *
 * WHY BLUEPRINT OWNS THIS AT ALL
 *
 * The Training Matrix has three tiers. User and SuperAdmin need nothing extra
 * — their own records, or every department. Admin is the one that needs a
 * scope, and that scope lives on the person's own row in the Training
 * Matrix's employee list, in a multi-choice column. Until now the only way to
 * set it was editing SharePoint by hand, which is why exactly two people have
 * one. Permissions belong where permissions are managed.
 *
 * HOW A PERSON IS FOUND, AND WHY IT IS TWO READS
 *
 * The Training Matrix joins a person to their row with a SharePoint REST
 * filter on an expanded person column: User/EMail eq '<email>'. Graph cannot
 * do that. It returns a person column as a bare UserLookupId integer, and
 * refuses to filter on it at all — "not indexed", with an override header
 * named HonorNonIndexedQueriesWarningMayFailRandomly, which is not a thing to
 * put under a permissions screen.
 *
 * So: read the site's User Information List to map lookup id to email, read
 * the employee list, and match in memory. Measured on the live site — 116
 * user rows, 101 employees — so this is two small reads, not a scan.
 *
 * WHAT WILL GO WRONG
 *
 * Five of those 101 rows have no resolvable email: either no account in the
 * User column, or a lookup id belonging to someone whose account has gone.
 * Those people cannot be scoped from here and the caller is told which of the
 * two it is, because the fix differs.
 */

import { GraphError, type TokenProvider } from '../sharepoint/GraphClient';

const GRAPH = 'https://graph.microsoft.com/v1.0';

/** The Training Matrix's own site and lists. Its employee list is not
 *  Blueprint's, and deliberately so — see project notes: TrainingApp_Employees
 *  is the Training Matrix's and is not to be read as a people register. */
export interface ITrainingSite {
  siteId: string;
  employeesListId: string;
  departmentsListTitle: string;
}

export interface ITrainingScope {
  /** The employee list item to write back to. */
  itemId: string;
  name: string;
  departments: string[];
}

/** Why a person cannot be scoped, when they cannot. The two cases need
 *  different fixes, so they are not collapsed into one message. */
export type ScopeRefusal = 'no-employee-row' | 'row-has-no-account';

export class TrainingScopeClient {
  constructor(
    private readonly getToken: TokenProvider,
    private readonly site: ITrainingSite,
    private readonly scopes: string[] = ['Sites.ReadWrite.All'],
  ) {}

  private async request<T>(url: string, init: RequestInit = {}): Promise<T> {
    const token = await this.getToken(this.scopes);
    const res = await fetch(url.startsWith('http') ? url : `${GRAPH}${url}`, {
      ...init,
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
        ...init.headers,
      },
    });
    if (!res.ok) {
      const body = await res.json().catch(() => null);
      const detail = (body as { error?: { message?: string } } | null)?.error?.message;
      throw new GraphError(
        detail ? `Graph ${res.status}: ${detail}` : `Graph ${res.status} on ${url}`,
        res.status,
        body,
      );
    }
    if (res.status === 204) return undefined as T;
    return (await res.json()) as T;
  }

  /** The departments the Training Matrix knows, from its own list rather than
   *  a copy here — the choice column and this list hold the same ten, checked,
   *  and a copy would be the one that drifts. */
  async departments(): Promise<string[]> {
    const r = await this.request<{ value: { fields: { Title?: string } }[] }>(
      `/sites/${this.site.siteId}/lists/${encodeURIComponent(this.site.departmentsListTitle)}`
      + `/items?$expand=fields($select=Title)&$top=200`,
    );
    return r.value
      .map((i) => (i.fields.Title || '').trim())
      .filter(Boolean)
      .sort((a, b) => a.localeCompare(b));
  }

  /** lookup id -> email, from the site's own user list. */
  private async emailByLookupId(): Promise<Map<string, string>> {
    const r = await this.request<{ value: { fields: { id?: string; EMail?: string } }[] }>(
      `/sites/${this.site.siteId}/lists/${encodeURIComponent('User Information List')}`
      + `/items?$expand=fields($select=id,EMail)&$top=999`,
    );
    const map = new Map<string, string>();
    r.value.forEach((i) => {
      const id = i.fields.id;
      const mail = (i.fields.EMail || '').trim().toLowerCase();
      if (id && mail) map.set(String(id), mail);
    });
    return map;
  }

  /** This person's row and what they currently administer, or why not. */
  async get(email: string, name?: string): Promise<ITrainingScope | ScopeRefusal> {
    const want = email.trim().toLowerCase();
    const [mails, emps] = await Promise.all([
      this.emailByLookupId(),
      this.request<{
        value: { id: string; fields: { Title?: string; UserLookupId?: string; AdminDepartments?: string[] } }[];
      }>(
        `/sites/${this.site.siteId}/lists/${this.site.employeesListId}`
        + `/items?$expand=fields&$top=999`,
      ),
    ]);

    const row = emps.value.find((i) => {
      const lid = i.fields.UserLookupId;
      return !!lid && mails.get(String(lid)) === want;
    });

    if (!row) {
      /* Which of the two it is, and it matters: a row that exists but is not
         joined to an account needs its User column filling in, where no row
         at all needs the person adding. Told apart by name, which is the only
         other thing both sides hold — deliberately only as a tiebreak for the
         message, never to decide who gets written to. */
      const wanted = (name || '').trim().toLowerCase();
      const orphan = !!wanted && emps.value.some((i) => {
        const lid = i.fields.UserLookupId;
        const unresolved = !lid || !mails.get(String(lid));
        return unresolved && (i.fields.Title || '').trim().toLowerCase() === wanted;
      });
      return orphan ? 'row-has-no-account' : 'no-employee-row';
    }

    return {
      itemId: row.id,
      name: row.fields.Title || '',
      /* Graph omits a multi-choice column entirely when it is empty, so an
         absent field means none rather than unknown. */
      departments: Array.isArray(row.fields.AdminDepartments) ? row.fields.AdminDepartments : [],
    };
  }

  /** Replace what they administer.
   *
   *  THE ANNOTATION IS NOT OPTIONAL. Graph will not infer the element type of
   *  a collection and refuses the whole request with a bare 400 "Invalid
   *  request" if one arrives without it — which is how writing this column
   *  broke silently in the Training Matrix itself once already. The same rule
   *  is written down in that app's pnpConfig.ts.
   */
  async set(itemId: string, departments: string[]): Promise<void> {
    await this.request<unknown>(
      `/sites/${this.site.siteId}/lists/${this.site.employeesListId}/items/${itemId}/fields`,
      {
        method: 'PATCH',
        body: JSON.stringify({
          'AdminDepartments@odata.type': 'Collection(Edm.String)',
          AdminDepartments: departments,
        }),
      },
    );
  }
}
